import moment from 'moment';
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import useSWR from 'swr';
import { Loader } from '../../components/loader/Loader';
import { useBreadcrumbs } from '../../contexts/breadcrumbsContext';
import { useTranslation } from '../../contexts/translation';
import { WorksheetsEditForm } from '../../forms/worksheetsDev/WorksheetsEditForm';
import { PageContentWrapper } from '../../layouts/contentWrapper/PageContentWrapper';
import { DetailPageParams } from '../../types/pageParams.types';
import { Task } from '../../types/task.types';
import { WorkSheet } from '../../types/workSheet.types';
import { API_URL, fetcher } from '../../utils/apiUrl';
import { swrConfig } from '../Assets/AssetsEdit';

const dateInputFormat = 'YYYY-MM-DD HH:mm:ss';

export const WorksheetsEdit = () => {
  const { translate } = useTranslation();
  const transFormName = 'WorkSheet.Update';
  const { setValue } = useBreadcrumbs();

  const { id } = useParams<DetailPageParams>();

  useEffect(() => {
    setValue({
      pageName: 'Edit workSheet',
      steps: [
        { name: 'Home', pathToCutCount: 3 },
        { name: 'WorkSheets', pathToCutCount: 2 },
      ],
    });
    return () => {
      setValue(null);
    };
  }, []);

  const { data: worksheet, isValidating: isValidatingWorksheet } = useSWR<WorkSheet>(
    `${API_URL.WORKSHEET_ITEM}?id=${id}`,
    fetcher,
    swrConfig,
  );

  const { data: task, isValidating: isValidatingTask } = useSWR<Task>(
    worksheet ? `${API_URL.TASK_ITEM}?id=${worksheet.taskId}` : null,
    fetcher,
    swrConfig,
  );

  const postProcessDuration = (activityDuration: string | null) => {
    if (!activityDuration) return null;
    // Rozdělíme dny od zbytku (pokud tam jsou)
    const parts = activityDuration.split('.');
    let days = 0;
    let timeStr = activityDuration;

    if (parts.length > 1) {
      days = parseInt(parts[0], 10);
      timeStr = parts[1];
    }

    // Rozdělíme čas na hodiny a minuty
    const [hours, minutes] = timeStr.split(':');

    // Přičteme dny k hodinám (1 den = 24 hodin)
    const totalHours = days * 24 + parseInt(hours, 10);

    // Vrátíme zformátovaný řetězec (přidáme nulu, pokud je hodin méně než 10)
    return `${totalHours.toString().padStart(2, '0')}:${minutes}`;
  };

  if (isValidatingWorksheet || !worksheet || isValidatingTask || !task) return <Loader />;

  return (
    <PageContentWrapper
      heading={`${translate!('Edit workSheet', transFormName)}: ${worksheet.taskCustomId}, ${
        worksheet.taskName
      }`}
    >
      <WorksheetsEditForm
        defaultFormData={{
          id: worksheet.id,
          topLevelParenName: worksheet.taskAssetTopLevelParenName,
          assetName: worksheet.taskAssetName,
          taskId: worksheet.taskId,
          taskName: worksheet.taskName,
          statusNameTranslated: worksheet.workSheetStatusNameTranslated,
          typeNameTranslated: worksheet.taskTypeNameTranslated,
          responsiblePersonName: task.responsiblePersonName,
          operatorId: worksheet.operatorId,
          capacityCategoryId: worksheet.workActivityCapacityCategoryId,
          workActivityGroupId: worksheet.workActivityGroupId,
          workActivityId: worksheet.workActivityId,
          documentTypeId: worksheet.taskTypeId,
          activityStarted: worksheet.activityStarted
            ? moment(worksheet.activityStarted).format(dateInputFormat)
            : undefined,
          activityFinished: worksheet.activityFinished
            ? moment(worksheet.activityFinished).format(dateInputFormat)
            : undefined,
          activityDuration: postProcessDuration(
            worksheet.activityDuration ? worksheet.activityDuration.toString() : null,
          ),
          faultTypeId: task.faultTypeId,
          description: worksheet.description,
        }}
        task={task}
        isEditableStatusFlag={
          worksheet.workSheetStatusId == 10 || worksheet.workSheetStatusId == 20
        }
      />
    </PageContentWrapper>
  );
};
